import React from 'react';
import { Play, Heart, Trash2, Edit3 } from 'lucide-react';
import { useAudio } from '../context/AudioContext';
import { StationArtwork } from './StationArtwork';

/**
 * صفّ إذاعة.
 *
 * كانت بطاقة بغلاف 16:10 وشارات متراكبة وظل — تسع بطاقتين في الشاشة فقط
 * وكلها بنفس الوزن البصري. الصفّ يعرض ستاً، ويجعل الاسم هو البطل.
 */
export const RadioCard = ({ station, onEdit }) => {
  const { currentStation, isPlaying, playStation, togglePlay, toggleFavorite, isFavorite, removeCustomStation } =
    useAudio();

  const isCurrent = currentStation?.id === station.id;
  const playing = isCurrent && isPlaying;
  const fav = isFavorite(station.id);

  const open = () => (isCurrent ? togglePlay() : playStation(station));
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={playing}
      aria-label={`${station.name}${station.reciter ? ` — ${station.reciter}` : ''}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      className={`group flex items-center gap-3 px-4 py-3 rule cursor-pointer transition-colors ${
        isCurrent ? 'bg-primary/[0.06]' : 'hover:bg-ink/[0.03]'
      }`}
    >
      <span className="relative w-12 h-12 rounded-md overflow-hidden flex-shrink-0 bg-surface-alt">
        {station.logo ? (
          <img src={station.logo} alt="" loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <StationArtwork station={station} className="w-full h-full" />
        )}
        <span
          className={`absolute inset-0 flex items-center justify-center transition-opacity ${
            isCurrent ? 'opacity-100 bg-primary/70' : 'opacity-0 group-hover:opacity-100 bg-surface-alt'
          }`}
        >
          {playing ? (
            <span className="flex items-end gap-[2px] h-3.5" aria-hidden="true">
              <span className="w-[2px] h-full bg-paper rounded-full animate-wave" />
              <span className="w-[2px] h-full bg-paper rounded-full animate-wave [animation-delay:0.15s]" />
              <span className="w-[2px] h-full bg-paper rounded-full animate-wave [animation-delay:0.3s]" />
            </span>
          ) : (
            <Play className="w-4 h-4 text-paper fill-current" aria-hidden="true" />
          )}
        </span>
      </span>

      <span className="flex-1 min-w-0">
        <span className={`block text-sm truncate tv:whitespace-normal tv:line-clamp-2 ${isCurrent ? 'font-semibold text-primary' : 'font-medium text-ink'}`}>
          {station.name}
        </span>
        <span className="block text-xs text-ink-muted truncate">
          {station.countryFlag ? `${station.countryFlag} ` : ''}
          {station.reciter || station.category || 'بث مباشر'}
        </span>
      </span>

      <span className="flex items-center gap-0.5 flex-shrink-0">
        {station.isCustom && (
          <>
            <button
              type="button"
              onClick={stop(() => onEdit?.(station))}
              aria-label="تعديل الإذاعة"
              className="w-8 h-8 rounded-md flex items-center justify-center text-ink-muted hover:text-primary hover:bg-ink/5 transition-colors"
            >
              <Edit3 className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={stop(() => {
                if (window.confirm(`حذف "${station.name}" من قائمتك؟`)) removeCustomStation(station.id);
              })}
              aria-label="حذف الإذاعة"
              className="w-8 h-8 rounded-md flex items-center justify-center text-ink-muted hover:text-live hover:bg-live/5 transition-colors"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={stop(() => toggleFavorite(station.id))}
          aria-label={fav ? 'إزالة من المفضلة' : 'إضافة للمفضلة'}
          aria-pressed={fav}
          className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-ink/5 transition-colors"
        >
          <Heart className={`w-4 h-4 ${fav ? 'fill-live text-live' : 'text-ink-muted'}`} aria-hidden="true" />
        </button>
      </span>
    </div>
  );
};
